/**
 * Pure computation helpers for the staff Production page.
 * Extracted from Production.jsx so the business logic is testable in isolation.
 */

// Order-centric summary across today's production batches
export const computeTodayProductionSummary = (batches) => {
  const categoryTotals = {};
  let totalNeeded = 0;
  let totalProduced = 0;
  let ordersComplete = 0;
  let totalOrders = 0;

  batches.forEach(batch => {
    batch.orders?.forEach(order => {
      totalOrders++;
      if (order.status === 'completed') ordersComplete++;

      order.items?.forEach(item => {
        const category = item.category || 'other';
        if (!categoryTotals[category]) {
          categoryTotals[category] = { category, quantity_needed: 0, quantity_produced: 0 };
        }
        const needed = item.quantity_needed || item.quantity || 0;
        categoryTotals[category].quantity_needed += needed;
        categoryTotals[category].quantity_produced += item.quantity_produced || 0;
        totalNeeded += needed;
        totalProduced += item.quantity_produced || 0;
      });
    });
  });

  return {
    total_donuts: totalNeeded,
    total_produced: totalProduced,
    total_orders: totalOrders,
    orders_complete: ordersComplete,
    categories: Object.values(categoryTotals).sort((a, b) => a.category.localeCompare(b.category)),
  };
};

// Category + product breakdown for the orders selected in the forecast day detail
export const computeFilteredForecastSummary = (forecastDayDetail, selectedOrders) => {
  if (!forecastDayDetail) return { total_donuts: 0, categories: [], products: [] };

  const categoryTotals = {};
  const productTotals = {};
  let totalDonuts = 0;

  // One row per product, so a walk-in's dozen glazed lands on the same line as
  // the wholesale glazed. A line with no product behind it keys on its own
  // name and stays visible as a one-off.
  const productKey = (item) => (item.product_id
    ? `p::${item.product_id}`
    : `one-off::${(item.product_name || '').trim().toLowerCase()}`);

  forecastDayDetail.orders_by_client
    .filter(order => selectedOrders.has(order.order_id))
    .forEach(order => {
      order.items.forEach(item => {
        const category = item.category || 'other';

        if (!categoryTotals[category]) {
          categoryTotals[category] = { category, quantity: 0 };
        }
        categoryTotals[category].quantity += item.quantity;

        const key = productKey(item);
        if (!productTotals[key]) {
          productTotals[key] = {
            product_name: item.product_name,
            product_id: item.product_id || '',
            category,
            quantity: 0,
            is_one_off: !item.product_id,
          };
        }
        productTotals[key].quantity += item.quantity;
        totalDonuts += item.quantity;
      });
    });

  return {
    total_donuts: totalDonuts,
    categories: Object.values(categoryTotals).sort((a, b) => a.category.localeCompare(b.category)),
    products: Object.values(productTotals).sort((a, b) => a.product_name.localeCompare(b.product_name)),
  };
};

// Seven day descriptors for the forecast week calendar
export const buildWeekDays = (weekStartDate, today) => {
  const days = [];
  // Add T12:00:00 to avoid timezone boundary issues
  const start = new Date(weekStartDate + 'T12:00:00');
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const dateStr = date.toISOString().split('T')[0];
    days.push({
      date: dateStr,
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum: date.getDate(),
      isToday: dateStr === today,
    });
  }
  return days;
};

// Per-order production progress (needed vs produced)
export const getOrderProgress = (order) => {
  const items = order.items || [];
  const totalNeeded = items.reduce((sum, i) => sum + (i.quantity_needed || i.quantity || 0), 0);
  const totalProduced = items.reduce((sum, i) => sum + (i.quantity_produced || 0), 0);
  // Only mark complete if there are items with actual quantities needed AND all are produced
  const allComplete = totalNeeded > 0 && items.every(i => {
    const needed = i.quantity_needed || i.quantity || 0;
    return needed > 0 && (i.quantity_produced || 0) >= needed;
  });
  return {
    totalNeeded,
    totalProduced,
    allComplete,
    percentage: totalNeeded > 0 ? Math.round((totalProduced / totalNeeded) * 100) : 0,
  };
};
